import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  MdPerson,
  MdArrowBack,
  MdLocationOn,
  MdAdd,
  MdClose,
  MdEdit,
  MdStar,
  MdStarBorder,
  MdCheckCircle,
  MdPhone,
  MdEmail,
  MdErrorOutline,
  MdLock,
  MdFingerprint,
} from "react-icons/md";
import { BsTrash } from "react-icons/bs";
import { createCustomer } from "../../services/customer.service";
import { toast } from "react-toastify";
import { State } from "country-state-city";
import {
  getAddresses,
  addAddress,
  updateAddress,
  deleteAddress,
  fetchCityStateByPincode,
} from "../../services/address.service";
import { t } from "i18next";
import { storeSettingsService } from "../../services/storeSettings.service";
import { useSelector } from "react-redux";
import { getStorefrontPath } from "./storefrontPaths";
import { getFingerprint } from "../../helpers/fingerprint.js";
import { startRegistration } from "@simplewebauthn/browser";
import {
  generateStorePasskeyRegistrationOptions,
  verifyStorePasskeyRegistration,
  updateCustomerProfileService,
} from "../../services/resellerCustomer.service";

// Theme hook (unchanged)
const useTheme = (businessUuid) => {
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTheme = async () => {
      try {
        setLoading(true);
        if (businessUuid) {
          try {
            const settingsResponse =
              await storeSettingsService.getPublicStoreSettings(businessUuid);
            console.log("Theme settings loaded:", settingsResponse);

            if (settingsResponse.success && settingsResponse.data?.theme) {
              const themeData = settingsResponse.data.theme;
              console.log("Setting theme data:", themeData);
              setTheme(themeData);
              localStorage.setItem(
                `store_theme_${businessUuid}`,
                JSON.stringify(themeData),
              );
              setLoading(false);
              return;
            }
          } catch (settingsError) {
            console.error("Error loading theme from settings:", settingsError);
          }
        }

        const savedTheme = localStorage.getItem(`store_theme_${businessUuid}`);
        if (savedTheme) {
          try {
            const parsedTheme = JSON.parse(savedTheme);
            console.log("Loaded theme from localStorage:", parsedTheme);
            setTheme(parsedTheme);
          } catch (e) {
            console.error("Error parsing saved theme:", e);
          }
        } else {
          console.log("Using default theme");
          setTheme({
            themeName: "Modern Indigo",
            fontFamily: "Inter",
            primaryColor: "#4f46e5",
            secondaryColor: "#10b981",
            backgroundColor: "#f9fafb",
            buttonColor: "#4f46e5",
            textColor: "#1f2937",
            headingColor: "#111827",
            borderRadius: "12px",
            buttonStyle: "rounded",
            cardStyle: "shadow",
            shadowColor: "rgba(0,0,0,0.1)",
          });
        }
      } catch (error) {
        console.error("Error loading theme:", error);
        setTheme({
          themeName: "Modern Indigo",
          fontFamily: "Inter",
          primaryColor: "#4f46e5",
          secondaryColor: "#10b981",
          backgroundColor: "#f9fafb",
          buttonColor: "#4f46e5",
          textColor: "#1f2937",
          headingColor: "#111827",
          borderRadius: "12px",
          buttonStyle: "rounded",
          cardStyle: "shadow",
          shadowColor: "rgba(0,0,0,0.1)",
        });
      } finally {
        setLoading(false);
      }
    };

    if (businessUuid) {
      loadTheme();
    }
  }, [businessUuid]);

  const isDark = (color) => {
    if (!color) return false;
    let hex = color.replace("#", "");
    if (hex.length === 3)
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
  };

  const darkMode = isDark(theme?.backgroundColor);

  return { theme, loading, darkMode };
};

// Helper function to adjust color brightness (unchanged)
const adjustColor = (color, amount) => {
  let usePound = false;
  if (color[0] === "#") {
    color = color.slice(1);
    usePound = true;
  }
  const num = parseInt(color, 16);
  let r = (num >> 16) + amount;
  if (r > 255) r = 255;
  else if (r < 0) r = 0;
  let b = ((num >> 8) & 0x00ff) + amount;
  if (b > 255) b = 255;
  else if (b < 0) b = 0;
  let g = (num & 0x0000ff) + amount;
  if (g > 255) g = 255;
  else if (g < 0) g = 0;
  return (
    (usePound ? "#" : "") +
    (g | (b << 8) | (r << 16)).toString(16).padStart(6, "0")
  );
};

// Theme-aware components (unchanged)
const ThemedCard = ({
  children,
  className = "",
  theme,
  variant = "default",
  darkMode = false,
}) => {
  const cardStyles = {
    default: {
      backgroundColor: darkMode
        ? adjustColor(theme?.backgroundColor || "#1f2937", 10)
        : "#ffffff",
      borderColor: darkMode ? "rgba(255,255,255,0.1)" : "#e5e7eb",
      color: theme?.textColor || (darkMode ? "#f3f4f6" : "#1f2937"),
    },
    primary: {
      backgroundColor:
        theme?.backgroundColor || (darkMode ? "#111827" : "#ffffff"),
      borderColor: darkMode ? "rgba(255,255,255,0.1)" : "#e5e7eb",
      color: theme?.textColor || (darkMode ? "#f3f4f6" : "#1f2937"),
    },
  };

  const style = cardStyles[variant] || cardStyles.default;

  return (
    <div
      className={`rounded-lg border ${className}`}
      style={{
        ...style,
        fontFamily: theme?.fontFamily || "Inter",
        borderRadius: theme?.borderRadius || "12px",
        boxShadow:
          theme?.cardStyle === "shadow"
            ? `0 1px 3px 0 ${theme?.shadowColor || "rgba(0, 0, 0, 0.1)"}, 0 1px 2px 0 rgba(0, 0, 0, 0.06)`
            : "none",
      }}
    >
      {children}
    </div>
  );
};

// Address Form Dialog Component with responsive improvements
function AddressFormDialog({
  open,
  onClose,
  onSubmit,
  initialAddress = "",
  theme,
  darkMode = false,
}) {
  const [states, setStates] = useState([]);
  const defaultCountryCode = "IN";

  useEffect(() => {
    const stateList = State.getStatesOfCountry(defaultCountryCode).map(
      (state) => ({
        label: state.name,
        value: state.isoCode,
      }),
    );
    setStates(stateList);
  }, []);

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    phone: Yup.string()
      .required("Mobile number is required")
      .matches(/^[6-9]\d{9}$/, "Enter valid 10-digit mobile number"),
    pincode: Yup.string()
      .required("Pincode is required")
      .matches(/^\d{6}$/, "Pincode must be 6 digits"),
    locality: Yup.string().required("Locality is required"),
    addressLine1: Yup.string()
      .required("Address is required")
      .min(10, "Minimum 10 characters"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
  });

  const parseInitialAddress = (addr) => {
    console.log("addr", addr);
    if (!addr) return { address: "", city: "", state: "", pincode: "" };
    if (typeof addr === "object") {
      return {
        name: addr.name || "",
        addressLine1: addr.addressLine1 || "",
        addressLine2: addr.addressLine2 || "",
        phone: addr.phone || "",
        locality: addr.locality || "",
        city: addr.city || "",
        state: addr.state || "",
        pincode: addr.postalCode || "",
      };
    }
    return { address: addr, city: "", state: "", pincode: "" };
  };

  const initialValues = parseInitialAddress(initialAddress);

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 ${darkMode ? "bg-black/70" : "bg-black/50"}`}
      style={{ fontFamily: theme?.fontFamily || "Inter" }}
    >
      <div
        className={`${darkMode ? "bg-gray-900 border border-gray-700 shadow-3xl" : "bg-white shadow-2xl"} rounded-xl sm:rounded-2xl w-full max-w-md mx-auto overflow-hidden`}
        style={{
          borderRadius: theme?.borderRadius || "12px",
        }}
      >
        <div
          className={`p-4 sm:p-6 border-b flex justify-between items-center ${darkMode ? "bg-gray-800 border-gray-700" : ""}`}
          style={{
            backgroundColor: !darkMode
              ? theme?.primaryColor
                ? `${theme.primaryColor}10`
                : "#f3f4f6"
              : undefined,
            borderColor: darkMode ? undefined : "#e5e7eb",
          }}
        >
          <h2
            className={`text-lg sm:text-xl font-semibold flex items-center ${darkMode ? "text-white" : "text-gray-900"}`}
            style={{ fontFamily: theme?.fontFamily || "Inter" }}
          >
            <MdLocationOn
              className='mr-1.5 sm:mr-2 text-xl sm:text-2xl'
              style={{ color: theme?.primaryColor || "#4f46e5" }}
            />
            {initialAddress
              ? t("ADDRESS_FORM.EDIT_ADDRESS")
              : t("ADDRESS_FORM.ADD_NEW_ADDRESS")}
          </h2>
          <button
            onClick={onClose}
            className={`p-1.5 sm:p-1 rounded-full transition-colors duration-200 ${darkMode ? "text-gray-400 hover:text-white hover:bg-gray-700" : "text-gray-500 hover:bg-gray-100"}`}
          >
            <MdClose className='text-xl sm:text-2xl' />
          </button>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={(values) => {
            const countryName = "India";
            onSubmit({
              values,
              city: values.city.trim(),
              state: values.state.trim(),
              country: countryName,
              pincode: values.pincode.trim(),
            });
          }}
        >
          {({
            errors,
            touched,
            isValid,
            dirty,
            handleChange,
            handleBlur,
            values,
            setFieldValue,
          }) => (
            <Form>
              <div className='p-3 sm:p-4 max-h-[60vh] sm:max-h-[70vh] overflow-y-auto'>
                <div className='space-y-3 sm:space-y-4'>
                  {/* Name & Mobile */}
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4'>
                    <div>
                      <label
                        className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                        style={{ fontFamily: theme?.fontFamily || "Inter" }}
                      >
                        {t("ADDRESS_FORM.NAME")}
                      </label>
                      <input
                        type='text'
                        name='name'
                        placeholder={t("ADDRESS_FORM.NAME_PLACEHOLDER")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.name}
                        className={`w-full p-2.5 sm:p-3 border rounded-lg sm:rounded-xl focus:ring-2 focus:outline-none focus:border-blue-500 text-sm ${
                          errors.name && touched.name
                            ? "border-red-500"
                            : darkMode
                              ? "border-gray-600"
                              : "border-gray-300"
                        } ${darkMode ? "bg-gray-800 text-gray-100 placeholder-gray-500" : ""}`}
                        style={{
                          borderRadius: theme?.borderRadius || "10px",
                          fontFamily: theme?.fontFamily || "Inter",
                          height: "42px",
                          backgroundColor: darkMode ? "#1f2937" : undefined,
                          color: darkMode ? "#f3f4f6" : undefined,
                        }}
                      />
                      {errors.name && touched.name && (
                        <div className='text-red-500 text-[10px] sm:text-xs mt-1 sm:mt-1.5'>
                          {errors.name}
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                        style={{ fontFamily: theme?.fontFamily || "Inter" }}
                      >
                        {t("ADDRESS_FORM.MOBILE_NUMBER")}
                      </label>
                      <input
                        type='text'
                        name='phone'
                        placeholder={t("ADDRESS_FORM.MOBILE_PLACEHOLDER")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.phone}
                        maxLength={10}
                        className={`w-full p-2.5 sm:p-3 border rounded-lg sm:rounded-xl focus:ring-2 focus:outline-none focus:border-blue-500 text-sm ${
                          errors.phone && touched.phone
                            ? "border-red-500"
                            : darkMode
                              ? "border-gray-600"
                              : "border-gray-300"
                        } ${darkMode ? "bg-gray-800 text-gray-100 placeholder-gray-500" : ""}`}
                        style={{
                          borderRadius: theme?.borderRadius || "10px",
                          fontFamily: theme?.fontFamily || "Inter",
                          height: "42px",
                          backgroundColor: darkMode ? "#1f2937" : undefined,
                          color: darkMode ? "#f3f4f6" : undefined,
                        }}
                      />
                      {errors.phone && touched.phone && (
                        <div className='text-red-500 text-[10px] sm:text-xs mt-1 sm:mt-1.5'>
                          {errors.phone}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pincode & Locality */}
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4'>
                    <div>
                      <label
                        className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                        style={{ fontFamily: theme?.fontFamily || "Inter" }}
                      >
                        {t("ADDRESS_FORM.PINCODE")}
                      </label>
                      <input
                        type='text'
                        name='pincode'
                        placeholder={t("ADDRESS_FORM.PINCODE_PLACEHOLDER")}
                        value={values.pincode}
                        onChange={async (e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          setFieldValue("pincode", value);

                          if (value.length === 6) {
                            const location =
                              await fetchCityStateByPincode(value);
                            if (location) {
                              setFieldValue("city", location.city);
                              setFieldValue("state", location.state);
                            }
                          }
                        }}
                        onBlur={handleBlur}
                        maxLength={6}
                        className={`w-full p-2.5 sm:p-3 border rounded-lg sm:rounded-xl focus:ring-2 focus:outline-none focus:border-blue-500 text-sm ${
                          errors.pincode && touched.pincode
                            ? "border-red-500"
                            : darkMode
                              ? "border-gray-600"
                              : "border-gray-300"
                        } ${darkMode ? "bg-gray-800 text-gray-100 placeholder-gray-500" : ""}`}
                        style={{
                          borderRadius: theme?.borderRadius || "10px",
                          fontFamily: theme?.fontFamily || "Inter",
                          height: "42px",
                          backgroundColor: darkMode ? "#1f2937" : undefined,
                          color: darkMode ? "#f3f4f6" : undefined,
                        }}
                      />
                      {errors.pincode && touched.pincode && (
                        <div className='text-red-500 text-[10px] sm:text-xs mt-1 sm:mt-1.5'>
                          {errors.pincode}
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                        style={{ fontFamily: theme?.fontFamily || "Inter" }}
                      >
                        {t("ADDRESS_FORM.LOCALITY")}
                      </label>
                      <input
                        type='text'
                        name='locality'
                        placeholder={t("ADDRESS_FORM.LOCALITY_PLACEHOLDER")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.locality}
                        className={`w-full p-2.5 sm:p-3 border rounded-lg sm:rounded-xl focus:ring-2 focus:outline-none focus:border-blue-500 text-sm ${
                          errors.locality && touched.locality
                            ? "border-red-500"
                            : darkMode
                              ? "border-gray-600"
                              : "border-gray-300"
                        } ${darkMode ? "bg-gray-800 text-gray-100 placeholder-gray-500" : ""}`}
                        style={{
                          borderRadius: theme?.borderRadius || "10px",
                          fontFamily: theme?.fontFamily || "Inter",
                          height: "42px",
                          backgroundColor: darkMode ? "#1f2937" : undefined,
                          color: darkMode ? "#f3f4f6" : undefined,
                        }}
                      />
                      {errors.locality && touched.locality && (
                        <div className='text-red-500 text-[10px] sm:text-xs mt-1 sm:mt-1.5'>
                          {errors.locality}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Address Line 1 */}
                  <div>
                    <label
                      className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                      style={{ fontFamily: theme?.fontFamily || "Inter" }}
                    >
                      {t("ADDRESS_FORM.ADDRESS_LABEL")}
                    </label>
                    <textarea
                      name='addressLine1'
                      placeholder={t("ADDRESS_FORM.ADDRESS_PLACEHOLDER")}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.addressLine1}
                      rows='2'
                      className={`w-full p-2.5 sm:p-3 border rounded-lg sm:rounded-xl focus:ring-2 focus:outline-none focus:border-blue-500 resize-none text-sm ${
                        errors.addressLine1 && touched.addressLine1
                          ? "border-red-500"
                          : darkMode
                            ? "border-gray-600"
                            : "border-gray-300"
                      } ${darkMode ? "bg-gray-800 text-gray-100 placeholder-gray-500" : ""}`}
                      style={{
                        borderRadius: theme?.borderRadius || "10px",
                        fontFamily: theme?.fontFamily || "Inter",
                        minHeight: "65px",
                        backgroundColor: darkMode ? "#1f2937" : undefined,
                        color: darkMode ? "#f3f4f6" : undefined,
                      }}
                    />
                    {errors.addressLine1 && touched.addressLine1 && (
                      <div className='text-red-500 text-[10px] sm:text-xs mt-1 sm:mt-1.5'>
                        {errors.addressLine1}
                      </div>
                    )}
                  </div>

                  {/* Address Line 2 */}
                  <div>
                    <label
                      className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                      style={{ fontFamily: theme?.fontFamily || "Inter" }}
                    >
                      {t("ADDRESS_FORM.LANDMARK")}
                    </label>
                    <input
                      type='text'
                      name='addressLine2'
                      placeholder={t("ADDRESS_FORM.LANDMARK_PLACEHOLDER")}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.addressLine2}
                      className={`w-full p-2.5 sm:p-3 border rounded-lg sm:rounded-xl focus:ring-2 focus:outline-none focus:border-blue-500 text-sm ${
                        errors.addressLine2 && touched.addressLine2
                          ? "border-red-500"
                          : darkMode
                            ? "border-gray-600"
                            : "border-gray-300"
                      } ${darkMode ? "bg-gray-800 text-gray-100 placeholder-gray-500" : ""}`}
                      style={{
                        borderRadius: theme?.borderRadius || "10px",
                        fontFamily: theme?.fontFamily || "Inter",
                        height: "42px",
                        backgroundColor: darkMode ? "#1f2937" : undefined,
                        color: darkMode ? "#f3f4f6" : undefined,
                      }}
                    />
                    {errors.addressLine2 && touched.addressLine2 && (
                      <div className='text-red-500 text-[10px] sm:text-xs mt-1 sm:mt-1.5'>
                        {errors.addressLine2}
                      </div>
                    )}
                  </div>

                  {/* City & State */}
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4'>
                    <div>
                      <label
                        className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                        style={{ fontFamily: theme?.fontFamily || "Inter" }}
                      >
                        {t("ADDRESS_FORM.CITY")}
                      </label>
                      <input
                        type='text'
                        name='city'
                        placeholder={t("ADDRESS_FORM.CITY_PLACEHOLDER")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.city}
                        className={`w-full p-2.5 sm:p-3 border rounded-lg sm:rounded-xl focus:ring-2 focus:outline-none focus:border-blue-500 text-sm ${
                          errors.city && touched.city
                            ? "border-red-500"
                            : darkMode
                              ? "border-gray-600"
                              : "border-gray-300"
                        } ${darkMode ? "bg-gray-800 text-gray-100 placeholder-gray-500" : ""}`}
                        style={{
                          borderRadius: theme?.borderRadius || "10px",
                          fontFamily: theme?.fontFamily || "Inter",
                          height: "42px",
                          backgroundColor: darkMode ? "#1f2937" : undefined,
                          color: darkMode ? "#f3f4f6" : undefined,
                        }}
                      />
                      {errors.city && touched.city && (
                        <div className='text-red-500 text-[10px] sm:text-xs mt-1 sm:mt-1.5'>
                          {errors.city}
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                        style={{ fontFamily: theme?.fontFamily || "Inter" }}
                      >
                        {t("ADDRESS_FORM.STATE")}
                      </label>
                      <select
                        name='state'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.state}
                        className={`w-full p-2.5 sm:p-3 border rounded-lg sm:rounded-xl focus:ring-2 focus:outline-none focus:border-blue-500 text-sm ${
                          errors.state && touched.state
                            ? "border-red-500"
                            : darkMode
                              ? "border-gray-600"
                              : "border-gray-300"
                        } ${darkMode ? "bg-gray-800 text-gray-100" : ""}`}
                        style={{
                          borderRadius: theme?.borderRadius || "10px",
                          fontFamily: theme?.fontFamily || "Inter",
                          height: "42px",
                          backgroundColor: darkMode ? "#1f2937" : undefined,
                          color: darkMode ? "#f3f4f6" : undefined,
                        }}
                      >
                        <option value=''>
                          {t("ADDRESS_FORM.SELECT_STATE")}
                        </option>
                        {states.map((state) => (
                          <option key={state.value} value={state.value}>
                            {state.label}
                          </option>
                        ))}
                      </select>
                      {errors.state && touched.state && (
                        <div className='text-red-500 text-[10px] sm:text-xs mt-1 sm:mt-1.5'>
                          {errors.state}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Country */}
                  <div>
                    <label
                      className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                      style={{ fontFamily: theme?.fontFamily || "Inter" }}
                    >
                      {t("ADDRESS_FORM.COUNTRY")}
                    </label>
                    <div className='relative'>
                      <input
                        type='text'
                        value='India'
                        disabled
                        className={`w-full p-2.5 sm:p-3 border rounded-lg sm:rounded-xl focus:ring-2 focus:outline-none focus:border-blue-500 text-sm ${darkMode ? "bg-gray-800 border-gray-700 text-gray-400" : "text-gray-500 bg-gray-50"}`}
                        style={{
                          borderRadius: theme?.borderRadius || "10px",
                          fontFamily: theme?.fontFamily || "Inter",
                          height: "42px",
                        }}
                      />
                      <MdCheckCircle className='absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-green-500 text-lg sm:text-xl' />
                    </div>
                  </div>

                  {/* Alternate Phone */}
                  <div>
                    <label
                      className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                      style={{ fontFamily: theme?.fontFamily || "Inter" }}
                    >
                      {t("ADDRESS_FORM.ALTERNATE_PHONE")}
                    </label>
                    <input
                      type='text'
                      name='alternatePhone'
                      placeholder={t(
                        "ADDRESS_FORM.ALTERNATE_PHONE_PLACEHOLDER",
                      )}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.alternatePhone}
                      maxLength={10}
                      className={`w-full p-2.5 sm:p-3 border rounded-lg sm:rounded-xl focus:ring-2 focus:outline-none focus:border-blue-500 text-sm ${
                        errors.alternatePhone && touched.alternatePhone
                          ? "border-red-500"
                          : darkMode
                            ? "border-gray-600"
                            : "border-gray-300"
                      } ${darkMode ? "bg-gray-800 text-gray-100 placeholder-gray-500" : ""}`}
                      style={{
                        borderRadius: theme?.borderRadius || "10px",
                        fontFamily: theme?.fontFamily || "Inter",
                        height: "42px",
                        backgroundColor: darkMode ? "#1f2937" : undefined,
                        color: darkMode ? "#f3f4f6" : undefined,
                      }}
                    />
                    {errors.alternatePhone && touched.alternatePhone && (
                      <div className='text-red-500 text-[10px] sm:text-xs mt-1 sm:mt-1.5'>
                        {errors.alternatePhone}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div
                className={`p-4 sm:p-6 border-t flex gap-3 sm:gap-4 ${darkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}
              >
                <button
                  type='button'
                  onClick={onClose}
                  className={`flex-1 py-2.5 sm:py-3 border rounded-lg sm:rounded-xl font-medium transition-colors duration-200 text-sm ${darkMode ? "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
                  style={{
                    borderRadius: theme?.borderRadius || "12px",
                    fontFamily: theme?.fontFamily || "Inter",
                  }}
                >
                  {t("ADDRESS_FORM.CANCEL")}
                </button>
                <button
                  type='submit'
                  disabled={!isValid || !dirty}
                  className='flex-1 py-2.5 sm:py-3 text-white rounded-lg sm:rounded-xl font-medium transition-all duration-200 shadow-md disabled:shadow-none text-sm'
                  style={{
                    backgroundColor: theme?.buttonColor || "#4f46e5",
                    borderRadius: theme?.borderRadius || "12px",
                    fontFamily: theme?.fontFamily || "Inter",
                    opacity: !isValid || !dirty ? 0.5 : 1,
                    "&:hover": {
                      backgroundColor: theme?.buttonColor
                        ? adjustColor(theme.buttonColor, -20)
                        : "#3730a3",
                    },
                    "&:disabled": {
                      backgroundColor: "#cbd5e1",
                    },
                  }}
                >
                  {t("ADDRESS_FORM.SAVE_ADDRESS")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

function ProfileDetailsPage() {
  const { businessName: urlBusinessName, bussinessUuid } = useParams();
  const navigate = useNavigate();
  const { theme, loading, darkMode } = useTheme(bussinessUuid);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [pendingPasskeySetup, setPendingPasskeySetup] = useState(false);
const location = useLocation();
  const businessData = useSelector((state) => state.bussiness.business);
  const business = Array.isArray(businessData)
    ? businessData.find((b) => b.bussinessUuid === bussinessUuid) ||
      businessData[0]
    : businessData;
  const displayBusinessName =
    urlBusinessName || business?.businessName || "Store";
 const handleBackNavigation = () => {
    const stateFrom = location?.state?.from;
    if (stateFrom && stateFrom !== location.pathname) {
      navigate(stateFrom);
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(
      getStorefrontPath({
        businessName: displayBusinessName,
        bussinessUuid,
      }),
      { replace: true }
    );
  };

  const userProfile = JSON.parse(
    localStorage.getItem(`user_${bussinessUuid}`) ||
      localStorage.getItem(`guest_${bussinessUuid}`) ||
      "null",
  );
  const isGuest = userProfile?.isGuest;

  const handleEnablePasskey = async (email = userProfile?.email) => {
    try {
      if (!email) {
        setPendingPasskeySetup(true);
        toast.info(t("PASSKEY.EMAIL_REQUIRED"));
        const emailField = document.querySelector('[name="email"]');
        emailField?.scrollIntoView({ behavior: "smooth", block: "center" });
        emailField?.focus({ preventScroll: true });
        return false;
      }

      setPendingPasskeySetup(false);

      const {
        data: { options, challengeKey },
      } = await generateStorePasskeyRegistrationOptions();

      // Diagnostic: ensure WebAuthn / Web Crypto APIs are available
      console.log("WebAuthn availability:", {
        PublicKeyCredential: typeof window.PublicKeyCredential !== "undefined",
        navigatorCredentials: typeof navigator.credentials !== "undefined",
        crypto: typeof window.crypto !== "undefined",
        subtle: !!(window.crypto && window.crypto.subtle),
      });

      if (
        typeof window.PublicKeyCredential === "undefined" ||
        typeof navigator.credentials === "undefined" ||
        typeof window.crypto === "undefined" ||
        !(window.crypto && window.crypto.subtle)
      ) {
        console.error("Crypto/WebAuthn APIs missing in this environment.");
        toast.error(
          "Passkey setup requires a supported browser and secure context.",
        );
        return;
      }

      const response = await startRegistration({ optionsJSON: options });

      const verificationResp = await verifyStorePasskeyRegistration({
        response,
        challengeKey,
      });

      if (verificationResp.data?.success) {
        toast.success(t("PASSKEY.REG_SUCCESS_LOGIN"));
        return true;
      } else {
        toast.error(t("PASSKEY.VERIFICATION_FAILED"));
        return false;
      }
    } catch (error) {
      console.error("Passkey setup error:", error);
      toast.error(t("PASSKEY.SETUP_FAILED"));
      return false;
    }
  };

  // Fetch addresses (unchanged)
  useEffect(() => {
    const fetchAddresses = async () => {
      if (userProfile?._id && bussinessUuid) {
        try {
          const res = await getAddresses(bussinessUuid, userProfile._id);
          if (res.data) {
            const formattedAddresses = res.data.map((addr) => ({
              ...addr,
              addressLine1: addr.addressLine1 || addr.formattedAddress || "",
              isDefault: addr.isDefault || false,
            }));
            setAddresses(formattedAddresses);

            const updatedProfile = {
              ...userProfile,
              addresses: formattedAddresses,
            };
            localStorage.setItem(
              `user_${bussinessUuid}`,
              JSON.stringify(updatedProfile),
            );
          }
        } catch (err) {
          console.error("Error fetching addresses:", err);
        }
      }
    };
    fetchAddresses();
  }, [userProfile?._id, bussinessUuid]);

  // Form validation schema (unchanged)
  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Full name is required")
      .min(2, "Name must be at least 2 characters"),
    phone: Yup.string()
      .matches(/^[0-9]{10}$/, "Phone number must be exactly 10 digits")
      .required("Phone number is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email address is required"),
  });

  const initialValues = {
    name: userProfile?.name || "",
    phone: userProfile?.customer_mobile || userProfile?.phone || "",
    email: userProfile?.email || "",
  };

  // Handle form submission (unchanged)
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const fingerprint = await getFingerprint();
      const profileToken = localStorage.getItem("customerAuthToken");
      const payload = {
        phone: values.phone,
        name: values.name,
        email: values.email,
      };

      const response = profileToken
        ? await updateCustomerProfileService(payload)
        : await createCustomer(bussinessUuid, {
            ...payload,
            customer_mobile: values.phone,
            addresses,
            returnExisting: true,
            fingerprint,
          });

      if (response?.data?.customer || response?.data) {
        const savedCustomer = response.data.customer || response.data;
        const updatedProfile = {
          ...userProfile,
          ...savedCustomer,
          name: values.name,
          customer_mobile: values.phone,
          email: values.email,
          addresses: addresses,
        };

        localStorage.setItem(
          `user_${bussinessUuid}`,
          JSON.stringify(updatedProfile),
        );

        toast.success("Profile updated successfully");
        if (pendingPasskeySetup) {
          await handleEnablePasskey(values.email);
        }
        navigate(
          getStorefrontPath({
            businessName: displayBusinessName,
            bussinessUuid,
          }),
        );
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle address submission (unchanged)
  const handleAddressSubmit = async ({
    values,
    city,
    state,
    country,
    pincode,
  }) => {
    try {
      console.log("handleAddressSubmit value1", values);
      const { name, phone, addressLine1, addressLine2, locality } = values;

      if (editingAddressIndex !== null) {
        console.log("handleAddressSubmit value", values);

        const addressId = addresses[editingAddressIndex]._id;
        const res = await updateAddress(
          bussinessUuid,
          userProfile._id,
          addressId,
          {
            addressLine1,
            addressLine2,
            locality,
            name,
            phone,
            city,
            state,
            country,
            postalCode: pincode,
            isDefault: addresses[editingAddressIndex].isDefault || false,
          },
        );

        if (res.data) {
          const newAddresses = [...addresses];
          newAddresses[editingAddressIndex] = res.data;
          setAddresses(newAddresses);
          toast.success("Address updated successfully");
        }
      } else {
        const isFirstAddress = addresses.length === 0;
        const res = await addAddress(bussinessUuid, userProfile._id, {
          addressLine1,
          addressLine2,
          locality,
          name,
          phone,
          city,
          state,
          country,
          postalCode: pincode,
          isDefault: isFirstAddress,
        });
        if (res.data) {
          let updatedAddresses;
          if (isFirstAddress) {
            updatedAddresses = [res.data];
          } else {
            updatedAddresses = [...addresses, res.data];
          }
          setAddresses(updatedAddresses);
          toast.success("Address added successfully");
        }
      }
    } catch (err) {
      console.error("Error saving address:", err);
      toast.error("Failed to save address");
    } finally {
      setAddressDialogOpen(false);
      setEditingAddressIndex(null);
    }
  };

  // Remove address function (unchanged)
  const removeAddress = async (index) => {
    try {
      const addressId = addresses[index]._id;
      const isDefault = addresses[index].isDefault;

      await deleteAddress(bussinessUuid, userProfile._id, addressId);

      const newAddresses = addresses.filter((_, i) => i !== index);

      if (isDefault && newAddresses.length > 0) {
        const firstAddressId = newAddresses[0]._id;
        await updateAddress(bussinessUuid, userProfile._id, firstAddressId, {
          isDefault: true,
        });

        newAddresses[0].isDefault = true;
      }

      setAddresses(newAddresses);
      toast.success("Address removed successfully");
    } catch (err) {
      console.error("Error deleting address:", err);
      toast.error("Failed to delete address");
    }
  };

  // Set default address function (unchanged)
  const setDefaultAddress = async (index) => {
    try {
      const addressId = addresses[index]._id;

      for (const addr of addresses) {
        if (addr.isDefault && addr._id !== addressId) {
          await updateAddress(bussinessUuid, userProfile._id, addr._id, {
            isDefault: false,
          });
        }
      }

      await updateAddress(bussinessUuid, userProfile._id, addressId, {
        isDefault: true,
      });

      const updatedAddresses = addresses.map((addr, i) => ({
        ...addr,
        isDefault: i === index,
      }));

      setAddresses(updatedAddresses);
      toast.success("Default address updated successfully");
    } catch (err) {
      console.error("Error setting default address:", err);
      toast.error("Failed to set default address");
    }
  };

  // Edit address function (unchanged)
  const editAddress = (index) => {
    console.log("index is editing", index);
    setEditingAddressIndex(index);
    setAddressDialogOpen(true);
  };

  const openAddAddressDialog = () => {
    if (isGuest) {
      toast.info("Sign in to add addresses");
      return;
    }
    setEditingAddressIndex(null);
    setAddressDialogOpen(true);
  };

  const closeAddressDialog = () => {
    setAddressDialogOpen(false);
    setEditingAddressIndex(null);
  };

  // Render addresses function with responsive improvements
  const renderAddresses = () => {
    if (addresses.length === 0) {
      return (
        <div
          className='text-center py-4 sm:py-6 px-3 sm:px-4 border-2 border-dashed rounded-xl sm:rounded-2xl border-gray-300'
          style={{
            borderRadius: theme?.borderRadius || "12px",
          }}
        >
          <MdLocationOn className='text-2xl sm:text-3xl mx-auto mb-1.5 sm:mb-2 text-gray-400' />
          <h3
            className='text-sm sm:text-base font-medium text-gray-500'
            style={{ fontFamily: theme?.fontFamily || "Inter" }}
          >
            {t("PROFILE_DETAILS.NO_ADDRESS_YET")}
          </h3>
          <p
            className='text-xs sm:text-sm mt-1 mb-2 sm:mb-3 text-gray-400'
            style={{ fontFamily: theme?.fontFamily || "Inter" }}
          >
            {t(
              "PROFILE_DETAILS.ADD_YOUR_FIRST_DELIVERY_ADDRESS_tO_GET_STARTED",
            )}
          </p>
          <button
            onClick={openAddAddressDialog}
            className='inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 rounded-lg text-xs sm:text-sm'
            style={{
              backgroundColor: theme?.buttonColor || "#4f46e5",
              color: "#ffffff",
              borderRadius:
                theme?.buttonStyle === "pill"
                  ? "9999px"
                  : theme?.buttonStyle === "rounded"
                    ? "8px"
                    : "4px",
              fontFamily: theme?.fontFamily || "Inter",
            }}
          >
            <MdAdd />
            {t("PROFILE_DETAILS.ADD_ADDRESS")}
          </button>
        </div>
      );
    }
    console.log("addresses", addresses);
    return (
      <div className='space-y-2.5 sm:space-y-3 max-h-[20rem] sm:max-h-[27rem] overflow-y-auto pr-1 sm:pr-2'>
        {addresses.map((address, index) => (
          <div
            key={address._id}
            className={`border rounded-lg p-3 sm:p-4 transition-all duration-200 hover:shadow-sm ${
              address.isDefault ? "border-2" : "hover:border-gray-300"
            }`}
            style={{
              borderColor: address.isDefault
                ? theme?.primaryColor || "#4f46e5"
                : "#e5e7eb",
              backgroundColor: address.isDefault
                ? theme?.primaryColor
                  ? `${theme.primaryColor}10`
                  : "#f3f4f6"
                : "#ffffff",
              borderRadius: theme?.borderRadius || "8px",
            }}
          >
            <div className='flex justify-between items-start mb-1.5 sm:mb-2'>
              {address.isDefault ? (
                <span
                  className='inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium'
                  style={{
                    backgroundColor: theme?.primaryColor
                      ? `${theme.primaryColor}20`
                      : "#f3f4f6",
                    color: theme?.primaryColor || "#4f46e5",
                    borderRadius: theme?.borderRadius || "9999px",
                  }}
                >
                  <MdStar className='mr-0.5 sm:mr-1 text-[12px] sm:text-base' />{" "}
                  Default
                </span>
              ) : (
                <button
                  onClick={() => !isGuest && setDefaultAddress(index)}
                  disabled={isGuest}
                  className={`inline-flex items-center text-[10px] sm:text-xs transition-colors duration-200 ${isGuest ? "text-gray-400 cursor-not-allowed" : "text-gray-500 hover:text-blue-600"}`}
                  title={
                    isGuest ? "Sign in to change default" : "Set as default"
                  }
                  style={{ fontFamily: theme?.fontFamily || "Inter" }}
                >
                  <MdStarBorder className='mr-0.5 sm:mr-1 text-[12px] sm:text-base' />{" "}
                  Set as default
                </button>
              )}
              <div className='flex gap-1.5 sm:gap-2'>
                <button
                  onClick={() => !isGuest && editAddress(index)}
                  disabled={isGuest}
                  className={`p-1 rounded-md text-xs sm:text-sm transition-colors duration-200 ${isGuest ? "text-gray-300 cursor-not-allowed" : "text-blue-600 hover:bg-blue-50 hover:text-blue-700"}`}
                  title={isGuest ? "Locked" : "Edit address"}
                  style={{ fontFamily: theme?.fontFamily || "Inter" }}
                >
                  <MdEdit className='text-sm sm:text-base' />
                </button>
                <button
                  onClick={() => !isGuest && removeAddress(index)}
                  disabled={isGuest}
                  className={`p-1 rounded-md text-xs sm:text-sm transition-colors duration-200 ${isGuest ? "text-gray-300 cursor-not-allowed" : "text-red-600 hover:bg-red-50 hover:text-red-700"}`}
                  title={isGuest ? "Locked" : "Remove address"}
                  style={{ fontFamily: theme?.fontFamily || "Inter" }}
                >
                  <BsTrash className='text-[10px] sm:text-xs' />
                </button>
              </div>
            </div>

            {/* Address Details Display */}
            <p
              className='font-medium text-xs sm:text-sm mb-0.5 sm:mb-1'
              style={{
                color: theme?.textColor || (darkMode ? "#f3f4f6" : "#111827"),
                fontFamily: theme?.fontFamily || "Inter",
              }}
            >
              {address?.name}
            </p>
            <p
              className='font-medium text-xs sm:text-sm mb-0.5 sm:mb-1'
              style={{
                color: theme?.textColor || (darkMode ? "#f3f4f6" : "#111827"),
                fontFamily: theme?.fontFamily || "Inter",
              }}
            >
              {address?.phone}
            </p>
            <p
              className='text-xs sm:text-sm mb-0.5 sm:mb-1'
              style={{
                color: darkMode ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.8)",
                fontFamily: theme?.fontFamily || "Inter",
              }}
            >
              {address?.addressLine1 || "Address not available"}
              {address?.addressLine2 && `, ${address.addressLine2}`}
            </p>
            <p
              className='text-xs sm:text-sm mb-0.5 sm:mb-1'
              style={{
                color: darkMode ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.8)",
                fontFamily: theme?.fontFamily || "Inter",
              }}
            >
              {address?.locality && `${address.locality}`}
            </p>
            <p
              className='text-[10px] sm:text-xs'
              style={{
                color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
                fontFamily: theme?.fontFamily || "Inter",
              }}
            >
              {address.city}, {address.state}, {address.country} -{" "}
              {address.postalCode}
            </p>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto'></div>
          <p className='mt-3 sm:mt-4 text-sm sm:text-base text-gray-600'>
            Loading profile...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className='min-h-screen'
      style={{
        backgroundColor:
          theme?.backgroundColor || (darkMode ? "#121212" : "#f9fafb"),
        fontFamily: theme?.fontFamily || "Inter",
        color: theme?.textColor || (darkMode ? "#f3f4f6" : "#1f2937"),
      }}
    >
      {/* Header - Responsive */}
      <div
        className={`shadow-md border-b ${darkMode ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"}`}
        style={{ fontFamily: theme?.fontFamily || "Inter" }}
      >
        <div className='max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 md:py-5 flex items-center'>
          <button
             onClick={handleBackNavigation}
            className='flex items-center mr-3 sm:mr-4 md:mr-6 transition-colors duration-200 font-medium text-xs sm:text-sm md:text-base'
            style={{
              color: theme?.primaryColor || "#4f46e5",
              fontFamily: theme?.fontFamily || "Inter",
            }}
          >
            <MdArrowBack className='text-base sm:text-lg md:text-xl mr-1 sm:mr-2' />
            <span className='hidden xs:inline'>
              {t("PROFILE_DETAILS.BACK_TO_STORE")}
            </span>
            <span className='inline xs:hidden'>Back</span>
          </button>
          <div className='flex items-center gap-2 sm:gap-3'>
            <div
              className='p-1.5 sm:p-2 rounded-lg'
              style={{
                backgroundColor: theme?.primaryColor
                  ? `${theme.primaryColor}10`
                  : "#dbeafe",
                borderRadius: theme?.borderRadius || "8px",
              }}
            >
              <MdPerson
                style={{ color: theme?.primaryColor || "#4f46e5" }}
                className='text-lg sm:text-xl md:text-2xl'
              />
            </div>
            <h1
              className='text-lg sm:text-xl md:text-2xl font-bold'
              style={{
                color: theme?.headingColor || "#111827",
                fontFamily: theme?.fontFamily || "Inter",
              }}
            >
              {t("PROFILE_DETAILS.PROFILE_DETAILS")}
            </h1>
          </div>
        </div>
      </div>

      {/* Content - Responsive layout */}
      <div className='max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8'>
        <div className='flex flex-col lg:flex-row gap-4 sm:gap-6 md:gap-8'>
          {/* Left Side - User Details */}
          <div className='w-full lg:w-2/5'>
            <ThemedCard
              className='p-4 sm:p-5 md:p-6 border lg:sticky lg:top-8'
              theme={theme}
              darkMode={darkMode}
            >
              <div className='mb-4 sm:mb-5 md:mb-6'>
                <h2
                  className='text-base sm:text-lg md:text-xl font-semibold flex items-center gap-1.5 sm:gap-2'
                  style={{
                    color: theme?.headingColor || "#111827",
                    fontFamily: theme?.fontFamily || "Inter",
                  }}
                >
                  <div
                    className='w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center'
                    style={{
                      backgroundColor: theme?.primaryColor
                        ? `${theme.primaryColor}10`
                        : "#dbeafe",
                      borderRadius: theme?.borderRadius || "9999px",
                    }}
                  >
                    <MdPerson
                      style={{ color: theme?.primaryColor || "#4f46e5" }}
                      className='text-sm sm:text-base md:text-lg'
                    />
                  </div>
                  {t("PROFILE_DETAILS.PERSONAL_INFORMATION")}
                </h2>
                <p
                  className='mt-0.5 sm:mt-1 text-xs sm:text-sm'
                  style={{
                    color: theme?.textColor + "80" || "#6b7280",
                    fontFamily: theme?.fontFamily || "Inter",
                  }}
                >
                  {t("PROFILE_DETAILS.UPDATE_YOUR_PERSONAL_DETAILS")}
                </p>
              </div>

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({
                  isSubmitting,
                  isValid,
                  handleChange,
                  handleBlur,
                  values,
                }) => (
                  <Form className='space-y-4 sm:space-y-5 md:space-y-6'>
                    {isGuest && (
                      <div
                        className={`${darkMode ? "bg-amber-950/30 border-amber-900/50" : "bg-amber-50 border-amber-200"} border rounded-lg sm:rounded-xl p-3 sm:p-4 mb-1.5 sm:mb-2 flex items-start gap-2 sm:gap-3`}
                      >
                        <MdErrorOutline className='text-amber-500 text-base sm:text-lg md:text-xl mt-0.5 flex-shrink-0' />
                        <div>
                          <p
                            className={`text-xs sm:text-sm font-semibold ${darkMode ? "text-amber-200" : "text-amber-900"}`}
                          >
                            Guest Mode
                          </p>
                          <p
                            className={`text-[10px] sm:text-xs ${darkMode ? "text-amber-300/80" : "text-amber-800"}`}
                          >
                            You are using a guest account. Profile updates and
                            address management are only available for registered
                            users.
                          </p>
                        </div>
                      </div>
                    )}
                    {/* Name Field */}
                    <div>
                      <label
                        className='block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2'
                        style={{
                          color: theme?.textColor || "#1f2937",
                          fontFamily: theme?.fontFamily || "Inter",
                        }}
                      >
                        {t("PROFILE_DETAILS.FULL_NAME")}
                      </label>
                      <div className='relative'>
                        <Field
                          type='text'
                          name='name'
                          disabled={isGuest}
                          className={`w-full py-2.5 pr-2.5 sm:py-3 sm:pr-3 md:py-4 md:pr-4 border rounded-lg sm:rounded-xl focus:ring-2 focus:border-blue-500 transition-all duration-200 pl-10 sm:pl-12 text-sm ${isGuest ? (darkMode ? "bg-gray-800/50 text-gray-500" : "bg-gray-50 text-gray-500") : darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"} ${isGuest ? "cursor-not-allowed" : ""}`}
                          placeholder='Enter your full name'
                          style={{
                            borderRadius: theme?.borderRadius || "12px",
                            fontFamily: theme?.fontFamily || "Inter",
                            color:
                              theme?.textColor ||
                              (darkMode ? "#f3f4f6" : "#1f2937"),
                          }}
                        />
                        <div className='absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none'>
                          <MdPerson className='text-base sm:text-lg md:text-xl text-gray-400' />
                        </div>
                      </div>
                      <ErrorMessage
                        name='name'
                        component='div'
                        className='text-red-500 text-[10px] sm:text-xs mt-1.5 sm:mt-2'
                      />
                    </div>

                    {/* Phone Field */}
                    <div>
                      <label
                        className='block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2'
                        style={{
                          color: theme?.textColor || "#1f2937",
                          fontFamily: theme?.fontFamily || "Inter",
                        }}
                      >
                        {t("PROFILE_DETAILS.PHONE_NUMBER")}
                      </label>
                      <Field name='phone'>
                        {({ field, form }) => (
                          <div className='relative'>
                            <div className='absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none'>
                              <MdPhone className='text-base sm:text-lg md:text-xl text-gray-400' />
                            </div>
                            <input
                              {...field}
                              type='tel'
                              onChange={(e) => {
                                const value = e.target.value
                                  .replace(/\D/g, "")
                                  .slice(0, 10);
                                form.setFieldValue("phone", value);
                              }}
                              disabled={isGuest}
                              className={`w-full pl-10 sm:pl-12 py-2.5 pr-2.5 sm:py-3 sm:pr-3 md:py-4 md:pr-4 border rounded-lg sm:rounded-xl focus:ring-2 focus:border-blue-500 transition-all duration-200 text-sm ${isGuest ? (darkMode ? "bg-gray-800/50 text-gray-500" : "bg-gray-50 text-gray-500") : darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"} ${isGuest ? "cursor-not-allowed" : ""}`}
                              placeholder='Enter your phone number'
                              style={{
                                borderRadius: theme?.borderRadius || "12px",
                                fontFamily: theme?.fontFamily || "Inter",
                                color:
                                  theme?.textColor ||
                                  (darkMode ? "#f3f4f6" : "#1f2937"),
                              }}
                            />
                          </div>
                        )}
                      </Field>
                      <ErrorMessage
                        name='phone'
                        component='div'
                        className='text-red-500 text-[10px] sm:text-xs mt-1.5 sm:mt-2'
                      />
                    </div>

                    {/* Email Field */}
                    <div>
                      <label
                        className='block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2'
                        style={{
                          color: theme?.textColor || "#1f2937",
                          fontFamily: theme?.fontFamily || "Inter",
                        }}
                      >
                        {t("PROFILE_DETAILS.EMAIL_ADDRESS")}
                      </label>
                      <div className='relative'>
                        <Field
                          type='email'
                          name='email'
                          disabled={isGuest}
                          className={`w-full py-2.5 pr-2.5 sm:py-3 sm:pr-3 md:py-4 md:pr-4 border rounded-lg sm:rounded-xl focus:ring-2 focus:border-blue-500 transition-all duration-200 pl-10 sm:pl-12 text-sm ${isGuest ? (darkMode ? "bg-gray-800/50 text-gray-500" : "bg-gray-50 text-gray-500") : darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"} ${isGuest ? "cursor-not-allowed" : ""}`}
                          placeholder='Enter your email address'
                          style={{
                            borderRadius: theme?.borderRadius || "12px",
                            fontFamily: theme?.fontFamily || "Inter",
                            color:
                              theme?.textColor ||
                              (darkMode ? "#f3f4f6" : "#1f2937"),
                          }}
                        />
                        <div className='absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none'>
                          <MdEmail className='text-base sm:text-lg md:text-xl text-gray-400' />
                        </div>
                      </div>
                      <ErrorMessage
                        name='email'
                        component='div'
                        className='text-red-500 text-[10px] sm:text-xs mt-1.5 sm:mt-2'
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className='flex gap-3 sm:gap-4 pt-4 sm:pt-5 md:pt-6'>
                      <button
                        type='button'
                        onClick={() =>
                          navigate(
                            getStorefrontPath({
                              businessName: displayBusinessName,
                              bussinessUuid,
                            }),
                          )
                        }
                        className={`flex-1 py-2 sm:py-2.5 md:py-3 border rounded-lg sm:rounded-xl font-medium transition-colors duration-200 text-xs sm:text-sm ${darkMode ? "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
                        style={{
                          borderRadius: theme?.borderRadius || "12px",
                          fontFamily: theme?.fontFamily || "Inter",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type='submit'
                        disabled={!isValid || isSubmitting || isGuest}
                        className={`flex-1 py-2 sm:py-2.5 md:py-3 text-white rounded-lg sm:rounded-xl font-medium transition-all duration-200 shadow-md disabled:shadow-none text-xs sm:text-sm ${isGuest ? "opacity-50 cursor-not-allowed" : ""}`}
                        style={{
                          backgroundColor: isGuest
                            ? "#94a3b8"
                            : theme?.buttonColor || "#4f46e5",
                          borderRadius: theme?.borderRadius || "12px",
                          fontFamily: theme?.fontFamily || "Inter",
                        }}
                      >
                        {isSubmitting ? (
                          <span className='flex items-center justify-center'>
                            <svg
                              className='animate-spin -ml-1 mr-1.5 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 text-white'
                              xmlns='http://www.w3.org/2000/svg'
                              fill='none'
                              viewBox='0 0 24 24'
                            >
                              <circle
                                className='opacity-25'
                                cx='12'
                                cy='12'
                                r='10'
                                stroke='currentColor'
                                strokeWidth='4'
                              ></circle>
                              <path
                                className='opacity-75'
                                fill='currentColor'
                                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                              ></path>
                            </svg>
                            {t("PROFILE_DETAILS.SAVING")}
                          </span>
                        ) : isGuest ? (
                          <span className='flex items-center justify-center gap-1.5 sm:gap-2'>
                            <MdLock className='text-xs sm:text-sm' /> Locked
                          </span>
                        ) : (
                          t("PROFILE_DETAILS.SAVE_CHANGES")
                        )}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </ThemedCard>

            {/* Passkey Settings */}
            {!isGuest && (
              <ThemedCard
                className='p-4 sm:p-5 md:p-6 border mt-4 sm:mt-6'
                theme={theme}
                darkMode={darkMode}
              >
                <div className='mb-4'>
                  <h2
                    className='text-base sm:text-lg md:text-xl font-semibold flex items-center gap-1.5 sm:gap-2'
                    style={{
                      color: theme?.headingColor || "#111827",
                      fontFamily: theme?.fontFamily || "Inter",
                    }}
                  >
                    <div
                      className='w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center'
                      style={{
                        backgroundColor: theme?.primaryColor
                          ? `${theme.primaryColor}10`
                          : "#dbeafe",
                        borderRadius: theme?.borderRadius || "9999px",
                      }}
                    >
                      <MdFingerprint
                        style={{ color: theme?.primaryColor || "#4f46e5" }}
                        className='text-sm sm:text-base md:text-lg'
                      />
                    </div>
                    {t("PASSKEY.SECURITY_SETTINGS")}
                  </h2>
                  <p
                    className='mt-0.5 sm:mt-1 text-xs sm:text-sm'
                    style={{
                      color: theme?.textColor + "80" || "#6b7280",
                      fontFamily: theme?.fontFamily || "Inter",
                    }}
                  >
                    {t("PASSKEY.SECURE_DESC_STORE")}
                  </p>
                </div>
                <button
                  type='button'
                  onClick={handleEnablePasskey}
                  className={`w-full py-2 sm:py-2.5 md:py-3 text-white rounded-lg sm:rounded-xl font-medium transition-all duration-200 shadow-md flex items-center justify-center gap-2`}
                  style={{
                    backgroundColor: theme?.buttonColor || "#4f46e5",
                    borderRadius: theme?.borderRadius || "12px",
                    fontFamily: theme?.fontFamily || "Inter",
                  }}
                >
                  <MdFingerprint className='text-lg' />
                  {t("PASSKEY.ENABLE")}
                </button>
              </ThemedCard>
            )}
          </div>

          {/* Right Side - Address Management */}
          <div className='w-full lg:w-3/5'>
            <ThemedCard
              className='p-4 sm:p-5 md:p-6 border h-fit lg:sticky lg:top-8'
              theme={theme}
              darkMode={darkMode}
            >
              <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 mb-3 sm:mb-4'>
                <div>
                  <h2
                    className='text-base sm:text-lg md:text-xl font-semibold flex items-center gap-1.5 sm:gap-2'
                    style={{
                      color: theme?.headingColor || "#111827",
                      fontFamily: theme?.fontFamily || "Inter",
                    }}
                  >
                    <div
                      className='w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center'
                      style={{
                        backgroundColor: theme?.primaryColor
                          ? `${theme.primaryColor}10`
                          : "#dbeafe",
                        borderRadius: theme?.borderRadius || "9999px",
                      }}
                    >
                      <MdLocationOn
                        style={{ color: theme?.primaryColor || "#4f46e5" }}
                        className='text-sm sm:text-base md:text-lg'
                      />
                    </div>
                    {t("PROFILE_DETAILS.DELIVERY_ADDRESS")}
                  </h2>
                  <p
                    className='text-[10px] sm:text-xs md:text-sm mt-0.5 sm:mt-1'
                    style={{
                      color: theme?.textColor + "80" || "#6b7280",
                      fontFamily: theme?.fontFamily || "Inter",
                    }}
                  >
                    {t("PROFILE_DETAILS.MANAGE_YOUR_DELIVERY_ADDRESS")}
                  </p>
                </div>
                <button
                  onClick={isGuest ? null : openAddAddressDialog}
                  disabled={isGuest}
                  className={`w-full sm:w-auto flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl font-medium shadow-md text-xs sm:text-sm ${isGuest ? "opacity-50 cursor-not-allowed" : ""}`}
                  style={{
                    backgroundColor: isGuest
                      ? "#94a3b8"
                      : theme?.buttonColor || "#4f46e5",
                    color: "#ffffff",
                    borderRadius:
                      theme?.buttonStyle === "pill"
                        ? "9999px"
                        : theme?.buttonStyle === "rounded"
                          ? "8px"
                          : "4px",
                    fontFamily: theme?.fontFamily || "Inter",
                  }}
                >
                  {isGuest ? (
                    <MdLock className='text-sm sm:text-base' />
                  ) : (
                    <MdAdd className='text-sm sm:text-base' />
                  )}
                  {isGuest
                    ? "Add Address Locked"
                    : t("PROFILE_DETAILS.ADD_NEW_ADDRESS")}
                </button>
              </div>

              {renderAddresses()}
            </ThemedCard>
          </div>
        </div>
      </div>

      {/* Address Form Dialog */}
      <AddressFormDialog
        open={addressDialogOpen}
        onClose={closeAddressDialog}
        onSubmit={handleAddressSubmit}
        initialAddress={
          editingAddressIndex !== null ? addresses[editingAddressIndex] : ""
        }
        theme={theme}
        darkMode={darkMode}
      />
    </div>
  );
}

export default ProfileDetailsPage;